import { createSlice } from "@reduxjs/toolkit";
import { safeGetItem } from "../../utils/safeStorage";
import {
  getUserInfo,
  postWellnessIndex,
  postNotificationsSubscription,
} from "../actions";
import { claimDailyBonus, getCoinBalance } from "../actions/coinsActions";
import {
  addPost,
  commentPost,
  recordPostShare,
  replyComment,
} from "../actions/groupsActions";
import { followUser } from "../actions/socialActions";
import { savePlanModePreference } from "../actions/dietPlanActions";
import { recordChallengeActivity } from "../actions/challengesActions";
import { clearPersistedChatUnread } from "../chatUnreadPersist";
import { POST_STATUS_ID } from "../../utils/postStatus";
import { MEMBER_PLAN, planLabel, planRank } from "../../utils/memberPlan";
import { readAuth, removeAuth, writeAuth } from "../../services/authStorage";
const safeJson = (s, fallback) => {
  try {
    return JSON.parse(s ?? "") ?? fallback;
  } catch {
    return fallback;
  }
};

// Window during which an optimistic premium flag, set client-side after a
// successful RC purchase, shadows any incoming "free" payload from the backend.
// RC webhook → backend reconciliation typically completes within a few seconds,
// occasionally tens of seconds under queue lag. Beyond 10 minutes we accept the
// canonical response: we'd rather show the truth than carry a stale optimistic
// flag forever (e.g. if the purchase was refunded immediately or RC failed to
// deliver).
const OPTIMISTIC_TTL_MS = 10 * 60 * 1000;

const isPremiumSub = (sub) =>
  sub?.subscription_type?.name === "premium" && sub?.is_enabled === true;

/**
 * Smart-merges an incoming `master_club_subscriber` payload against the current
 * Redux state. The problem this solves: after a native IAP purchase the paywall
 * flips Redux to premium *optimistically* (RC's customerInfo says the user paid;
 * the backend webhook is still in flight). The very next `getUserInfo()` HTTP
 * call almost always wins the race against RC's webhook, so the response payload
 * still reports the pre-purchase free state. Without this merge, the side menu
 * regresses from premium back to free within ~500ms of purchase and stays that
 * way until the webhook lands.
 *
 * Strategy:
 *   - If the incoming payload already reports premium, the backend has caught
 *     up — accept it wholesale, dropping any `_optimistic` markers.
 *   - Otherwise, if the current state holds an unexpired optimistic premium
 *     record, keep that synthesized subscription but accept every other field
 *     from the backend (wellness_index, push_subscriptions, profile, etc.).
 *   - Past the TTL we accept the canonical response unconditionally so a
 *     legitimately reverted state (refund, RC delivery failure) can take over.
 *
 * The `_optimistic` flag itself is only ever set by `markPremiumOptimistic`,
 * which is only dispatched from the native paywall purchase / restore flows —
 * Stripe / web profile loads bypass this guard entirely (no marker to respect).
 */
const mergeMasterClubSubscriber = (currentSubscriber, incomingSubscriber) => {
  const currentSub = currentSubscriber?.subscription;
  const startedAt = Date.parse(currentSub?._optimisticAt || 0);
  const stillFresh =
    currentSub?._optimistic === true &&
    Number.isFinite(startedAt) &&
    Date.now() - startedAt < OPTIMISTIC_TTL_MS;

  // Premium Lite is subscription type "premium" too: the plan decides whether
  // the backend has caught up (a stale Lite record right after upgrading to
  // Premium must not replace the optimistic Premium one).
  const incomingPlan =
    incomingSubscriber?.access_info?.plan ?? MEMBER_PLAN.PREMIUM;
  const reachedTarget =
    !stillFresh ||
    planRank(incomingPlan) >=
      planRank(currentSub?._optimisticPlan ?? MEMBER_PLAN.PREMIUM);

  if (isPremiumSub(incomingSubscriber?.subscription) && reachedTarget) {
    return incomingSubscriber;
  }

  if (currentSub?._optimistic) {
    if (stillFresh) {
      // Keep the synthesized premium subscription *and* the synthesized
      // premium subscription_label on `subscriber.user` — the avatar badge,
      // post header, and comment header read from the label first and would
      // regress to "free" if we accepted the incoming label here.
      const currentLabel = currentSubscriber?.user?.subscription_label;
      const incomingUser = incomingSubscriber?.user || null;
      const mergedUser = incomingUser
        ? {
            ...incomingUser,
            subscription_label:
              currentLabel?.name === "premium"
                ? currentLabel
                : incomingUser.subscription_label,
          }
        : currentSubscriber?.user;
      return {
        ...(incomingSubscriber || {}),
        subscription: currentSub,
        user: mergedUser,
        access_info:
          currentSubscriber?.access_info ?? incomingSubscriber?.access_info,
      };
    }
  }

  return incomingSubscriber;
};

// Mirror of the above for the top-level `state.user` (the snake_case field
// that some bootstraps populate with `subscription_label` directly). Same
// rules: incoming premium wins; otherwise, while the optimistic flag on
// master_club_subscriber.subscription is still fresh, keep the synthesized
// label so every fallback selector reads consistently.
const mergeUser = (currentSubscriber, currentUser, incomingUser) => {
  const currentSub = currentSubscriber?.subscription;
  if (!currentSub?._optimistic) return incomingUser;
  if (currentUser?.subscription_label?.name !== "premium") return incomingUser;

  const startedAt = Date.parse(currentSub._optimisticAt || 0);
  const stillFresh =
    Number.isFinite(startedAt) && Date.now() - startedAt < OPTIMISTIC_TTL_MS;
  if (!stillFresh) return incomingUser;

  // Backend already reports premium → accept wholesale.
  const incomingLabel = incomingUser?.subscription_label;
  if (incomingLabel?.name === "premium") return incomingUser;

  return incomingUser
    ? { ...incomingUser, subscription_label: currentUser.subscription_label }
    : currentUser;
};

const storedProfile = safeJson(readAuth("adoc_p"), null);
const storedAgreements = safeJson(readAuth("adoc_agreements"), null);

const masterClubState = storedProfile?.master_club_subscriber ?? null;
const adoc_t = safeGetItem("adoc_t");

const initialState = {
  token: adoc_t,
  user: storedProfile?.user,
  master_club_subscriber: masterClubState,
  // App-level config returned by getUserInfo (e.g. dieta_social_id)
  app: storedProfile?.app ?? null,
  // Holds the Tutor badge: shows Area Team → Percorso FREE.
  free_program_tutor: storedProfile?.free_program_tutor === true,
  // Adoc patient info: null for non-patients; { is_patient, has_plan_today }
  // for users who are also patients in the adoc system.
  adoc_patient: storedProfile?.adoc_patient ?? null,
  adoc_t,
  agreements: Array.isArray(storedAgreements) ? storedAgreements : null,

  getUserIsLoading: false,
  getUserError: false,
  userModal: {
    open: false,
  },
  postWellnessIndex: {
    error: null,
    isLoading: null,
    completed: false,
  },
  postNotificationsSubscription: {
    error: null,
    isLoading: null,
    completed: false,
  },
  coins: {
    balance: null,
    estimated_value: null,
    lastTransaction: null,
    // Slice of `balance` that is an unspent campaign bonus (Bonus DS DAY) plus
    // its deadline. Only the balance endpoint knows the split, so it stays null
    // until that resolves; the other coin-awarding actions leave it alone.
    bonus: null,
  },
  // Apple "Hide My Email": set right after a relay sign-in with incomplete
  // onboarding so the global recover interstitial can render over any route
  // (it can't live on /login — RouteAuth redirects authenticated users away).
  appleRecoverPending: false,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAdoc_t: (state, action) => {
      state.adoc_t = action.payload;
      const token = action.payload || null;
      state.token = token;
      if (token) writeAuth("adoc_t", token);
      else removeAuth("adoc_t");
    },
    setAppleRecoverPending: (state, action) => {
      state.appleRecoverPending = !!action.payload;
    },
    setAdoc_p: (state, action) => {
      const profile = action.payload || null;

      if (!profile) {
        removeAuth("adoc_p");
        removeAuth("adoc_agreements");
        state.user = null;
        state.master_club_subscriber = null;
        state.app = null;
        state.free_program_tutor = false;
        state.adoc_patient = null;
        return;
      }

      const mergedSubscriber = mergeMasterClubSubscriber(
        state.master_club_subscriber,
        action.payload.master_club_subscriber,
      );
      const mergedUser = mergeUser(
        state.master_club_subscriber,
        state.user,
        action.payload.user,
      );

      state.user = mergedUser;
      state.master_club_subscriber = mergedSubscriber;
      state.app = action.payload.app ?? null;
      state.free_program_tutor = action.payload.free_program_tutor === true;
      state.adoc_patient = action.payload.adoc_patient ?? null;
      // Update coin balance if present in payload
      if (action.payload.coin_balance !== undefined) {
        state.coins.balance = action.payload.coin_balance;
      }
      if (action.payload.coin_estimated_value !== undefined) {
        state.coins.estimated_value = action.payload.coin_estimated_value;
      }

      // Persist the *merged* subscriber + user so a reload doesn't resurrect
      // the stale free state when an optimistic flag was active at the time
      // of the write.
      writeAuth(
        "adoc_p",
        JSON.stringify({
          ...action.payload,
          master_club_subscriber: mergedSubscriber,
          user: mergedUser,
          adoc_patient: action.payload.adoc_patient ?? null,
        }),
      );
    },
    setAdoc_agreements: (state, action) => {
      const agreements = action.payload;
      state.agreements = agreements;
      if (Array.isArray(agreements)) {
        writeAuth("adoc_agreements", JSON.stringify(agreements));
      } else {
        removeAuth("adoc_agreements");
      }
    },
    updateUserProperty: (state, action) => {
      state[action.payload.stateKey][action.payload.key] = action.payload.value;
      writeAuth(
        "adoc_p",
        JSON.stringify({
          master_club_subscriber: state.master_club_subscriber,
          user: state.user,
          app: state.app,
        }),
      );
    },
    /**
     * Optimistically mark the user premium based on a successful RC purchase,
     * before the backend has finished processing the webhook. The RC SDK's
     * customerInfo response is the device-side source of truth at the moment
     * of purchase — Apple/Google have already charged the user and granted
     * the entitlement. The backend will catch up within seconds (RC fires the
     * webhook asynchronously), and the next getUserInfo refetch will overwrite
     * this synthetic state with the canonical record. Until then the UI shows
     * the correct premium tier instantly instead of stale "free" until the
     * user force-quits and reopens.
     *
     * Payload: { entitlement } where entitlement is RC's
     * customerInfo.entitlements.active.master_club_premium object, which
     * contains expirationDate (ISO string) and willRenew (bool).
     */
    markPremiumOptimistic: (state, action) => {
      const entitlement = action.payload?.entitlement;
      if (!entitlement || !state.master_club_subscriber) return;
      // Premium Lite: the plan bought ({ targetPlan }), default full Premium.
      const targetPlan =
        action.payload?.targetPlan === MEMBER_PLAN.PREMIUM_LITE
          ? MEMBER_PLAN.PREMIUM_LITE
          : MEMBER_PLAN.PREMIUM;

      const expiresAt =
        entitlement.expirationDate || entitlement.expirationDateMillis || null;
      const expiresAtIso =
        typeof expiresAt === "number"
          ? new Date(expiresAt).toISOString()
          : expiresAt;

      // Synthesize a subscription record that matches the shape the rest of the
      // app reads (mainly subscription.subscription_type.name === "premium" and
      // is_enabled / end_date / next_billing_at). Real fields populated by the
      // backend after webhook processing will overwrite these.
      const optimisticSubscription = {
        ...(state.master_club_subscriber.subscription || {}),
        is_enabled: true,
        canceled_at: null,
        end_date: expiresAtIso,
        next_billing_at: entitlement.willRenew === false ? null : expiresAtIso,
        subscription_type: {
          ...(state.master_club_subscriber.subscription?.subscription_type ||
            {}),
          name: "premium",
        },
        // Markers consumed by mergeMasterClubSubscriber: shadow incoming "free"
        // payloads from the backend until the RC webhook is processed (or the
        // TTL expires). Underscored keys are ignored by every existing selector.
        _optimistic: true,
        _optimisticAt: new Date().toISOString(),
        _optimisticPlan: targetPlan,
      };

      state.master_club_subscriber.subscription = optimisticSubscription;
      state.master_club_subscriber.access_info = {
        ...(state.master_club_subscriber.access_info || {}),
        level: "premium",
        plan: targetPlan,
        plan_label: planLabel(targetPlan),
      };

      // The avatar badge on the SocialProfile page (and post/comment headers)
      // reads `master_club_subscriber.user.subscription_label.name` *first* and
      // only falls back to `subscription.subscription_type.name`. Without this
      // patch the top-right MainHeader badge flips to "PREMIUM" but the avatar
      // badge stays "FREE" until the backend webhook reconciles. Synthesize a
      // matching label so every badge in the app reads consistently optimistic.
      // The real label (with the canonical color) is overwritten by the next
      // getUserInfo response that reports premium.
      if (state.master_club_subscriber.user) {
        state.master_club_subscriber.user = {
          ...state.master_club_subscriber.user,
          subscription_label: {
            ...(state.master_club_subscriber.user.subscription_label || {}),
            name: "premium",
          },
        };
      }
      // The same label is sometimes mirrored on the top-level `state.user.user`
      // (web-shop bootstraps populate it there). Mirror the patch so the
      // fallback selector also resolves to premium.
      if (state.user) {
        state.user = {
          ...state.user,
          subscription_label: {
            ...(state.user.subscription_label || {}),
            name: "premium",
          },
        };
      }

      writeAuth(
        "adoc_p",
        JSON.stringify({
          master_club_subscriber: state.master_club_subscriber,
          user: state.user,
          app: state.app,
        }),
      );
    },
    resetPostWellnessIndexError: (state) => {
      state.postWellnessIndex.error = false;
    },
    LogoutUser: (state) => {
      state.user = null;
      state.master_club_subscriber = null;
      state.adoc_t = null;
      state.coins.balance = null;
      state.coins.estimated_value = null;
      state.coins.lastTransaction = null;
      removeAuth("adoc_p");
      removeAuth("adoc_agreements");
      removeAuth("adoc_i");
      removeAuth("adoc_t");
      localStorage.removeItem("adoc_rt");
      clearPersistedChatUnread();
    },
    updateUserModal: (state, payload) => {
      state.userModal.open = payload.payload;
    },
    updateCoinBalance: (state, action) => {
      state.coins.balance = action.payload.balance;
      if (action.payload.estimated_value !== undefined) {
        state.coins.estimated_value = action.payload.estimated_value;
      }
    },
  },
  extraReducers: (builder) => {
    // Plan mode picked in Percorsi (Adoc + Premium): the backend answers with
    // the new adoc block, so header badge and Menu follow without a refetch.
    builder.addCase(savePlanModePreference.fulfilled, (state, action) => {
      state.adoc_patient = action.payload?.adoc_patient ?? state.adoc_patient;
    });
    // Daily "Accesso giornaliero" bonus.
    //
    // Two separate decisions, deliberately. `awarded_today` stays true for the
    // rest of the day, so the BALANCE is refreshed on every cold start.
    // `show_popup` is true for exactly one caller per member per day — the
    // backend decides it, not the device — so only that one seeds a
    // lastTransaction and thereby raises the popup.
    builder.addCase(claimDailyBonus.fulfilled, (state, action) => {
      const payload = action.payload;
      if (!payload) return;

      // The registration bonus wins when the server nominates this call to
      // celebrate it, and the server withholds the daily claim in that case —
      // so a brand-new member is congratulated on the +150 rather than the +3,
      // and still gets the daily popup tomorrow. Its coins were credited at
      // registration, long before this call, so nothing here touches them.
      const welcome = payload.welcome_bonus;
      if (welcome && welcome.points > 0) {
        if (welcome.balance_after > 0) state.coins.balance = welcome.balance_after;
        state.coins.lastTransaction = {
          points: welcome.points,
          balance_after: welcome.balance_after,
          source: "welcomeBonus",
        };
        return;
      }

      if (!payload.awarded_today || !(payload.points > 0)) return;

      const { points, balance_after } = payload;
      if (balance_after > 0) state.coins.balance = balance_after;

      if (!payload.show_popup) return;
      state.coins.lastTransaction = {
        points,
        balance_after,
        source: "dailyLogin",
        // Yesterday's total, for the summary line under the coins. Carried on
        // the transaction so the popup does not have to reach back into the
        // response that produced it.
        yesterdayPoints: Number(payload.yesterday_points) || 0,
      };
    });

    builder.addCase(getUserInfo.pending, (state) => {
      state.getUserIsLoading = true;
      state.getUserError = false;
    });
    builder.addCase(getUserInfo.fulfilled, (state, action) => {
      if (Array.isArray(action.payload.agreements)) {
        state.agreements = action.payload.agreements;
        localStorage.setItem(
          "adoc_agreements",
          JSON.stringify(action.payload.agreements),
        );
      }

      // Smart-merge guards against the post-purchase race where this fetch
      // resolves before the RC webhook has reconciled state on the backend.
      // See `mergeMasterClubSubscriber` for the full rationale.
      const mergedSubscriber = mergeMasterClubSubscriber(
        state.master_club_subscriber,
        action.payload.master_club_subscriber,
      );
      const mergedUser = mergeUser(
        state.master_club_subscriber,
        state.user,
        action.payload.user,
      );

      state.getUserIsLoading = false;
      state.getUserError = false;
      state.user = mergedUser;
      state.master_club_subscriber = mergedSubscriber;
      state.app = action.payload.app ?? null;
      state.free_program_tutor = action.payload.free_program_tutor === true;
      // Keep adoc_patient in sync on every profile refresh (adoc_only / badge).
      state.adoc_patient = action.payload.adoc_patient ?? null;
      // Update coin balance from profile response
      if (action.payload.coin_balance !== undefined) {
        state.coins.balance = action.payload.coin_balance;
      }
      if (action.payload.coin_estimated_value !== undefined) {
        state.coins.estimated_value = action.payload.coin_estimated_value;
      }
      // Persist the merged values so localStorage stays in sync with Redux
      // across reloads (otherwise a reload during the optimistic window would
      // resurrect the stale free state from the original payload).
      writeAuth(
        "adoc_p",
        JSON.stringify({
          ...action.payload,
          master_club_subscriber: mergedSubscriber,
          user: mergedUser,
          adoc_patient: action.payload.adoc_patient ?? null,
        }),
      );
    });
    builder.addCase(getUserInfo.rejected, (state, action) => {
      state.getUserIsLoading = false;
      state.getUserError = action.error.message;
    });
    builder.addCase(postWellnessIndex.pending, (state) => {
      state.postWellnessIndex.isLoading = true;
      state.postWellnessIndex.error = false;
    });
    builder.addCase(postWellnessIndex.fulfilled, (state, action) => {
      state.postWellnessIndex.isLoading = false;
      state.postWellnessIndex.error = false;
      state.postWellnessIndex.completed = true;
      state.master_club_subscriber.wellness_index = {
        date: new Date().toISOString().slice(0, 10),
      };
      writeAuth(
        "adoc_p",
        JSON.stringify({
          master_club_subscriber: state.master_club_subscriber,
          user: state.user,
          app: state.app,
        }),
      );
    });
    builder.addCase(postWellnessIndex.rejected, (state, action) => {
      state.postWellnessIndex.isLoading = false;
      state.postWellnessIndex.error = action.error.message;
    });

    builder.addCase(postNotificationsSubscription.pending, (state) => {
      state.postNotificationsSubscription.isLoading = true;
      state.postNotificationsSubscription.error = false;
    });
    builder.addCase(
      postNotificationsSubscription.fulfilled,
      (state, action) => {
        state.postNotificationsSubscription.isLoading = false;
        state.postNotificationsSubscription.error = false;
        state.postNotificationsSubscription.completed = true;
        const newSubscription = {
          subscription_key: JSON.stringify(action.meta.arg),
        };
        state.master_club_subscriber.push_subscriptions = [
          ...(state.master_club_subscriber.push_subscriptions || []),
          newSubscription,
        ];
        writeAuth(
        "adoc_p",
          JSON.stringify({
            master_club_subscriber: state.master_club_subscriber,
            user: state.user,
            app: state.app,
          }),
        );
      },
    );
    builder.addCase(postNotificationsSubscription.rejected, (state, action) => {
      state.postNotificationsSubscription.isLoading = false;
      state.postNotificationsSubscription.error = action.error.message;
    });

    // Update coin balance from balance endpoint
    builder.addCase(getCoinBalance.fulfilled, (state, action) => {
      state.coins.balance = action.payload.balance;
      if (action.payload.estimated_value !== undefined) {
        state.coins.estimated_value = action.payload.estimated_value;
      }
      // Older backends don't send this; treat its absence as "no bonus" rather
      // than leaving a stale one on screen.
      state.coins.bonus =
        action.payload.bonus?.quantity > 0 ? action.payload.bonus : null;
    });

    // Handle coin_transaction from commentPost
    builder.addCase(commentPost.fulfilled, (state, action) => {
      if (action.payload?.coin_transaction) {
        const { balance_after, points } = action.payload.coin_transaction;
        state.coins.balance = balance_after;
        // Store coin transaction for toast notification
        state.coins.lastTransaction = {
          points,
          balance_after,
          action: action.payload.coin_transaction.action,
        };
      }
    });

    // Handle coin_transaction from replyComment
    builder.addCase(replyComment.fulfilled, (state, action) => {
      if (action.payload?.coin_transaction) {
        const { balance_after, points } = action.payload.coin_transaction;
        state.coins.balance = balance_after;
        // Store coin transaction for toast notification
        state.coins.lastTransaction = {
          points,
          balance_after,
          action: action.payload.coin_transaction.action,
        };
      }
    });

    builder.addCase(recordPostShare.fulfilled, (state, action) => {
      if (action.payload?.coin_transaction) {
        const { id, balance_after, points } = action.payload.coin_transaction;
        state.coins.balance = balance_after;
        state.coins.lastTransaction = {
          id,
          points,
          balance_after,
          action: action.payload.coin_transaction.action,
          source: "postShare",
        };
      }
    });

    // Handle coin_transaction from followUser
    builder.addCase(followUser.fulfilled, (state, action) => {
      if (action.payload?.coin_transaction) {
        const { balance_after, points } = action.payload.coin_transaction;
        state.coins.balance = balance_after;
        // Store coin transaction for toast notification
        state.coins.lastTransaction = {
          points,
          balance_after,
          action: action.payload.coin_transaction.action,
        };
      }
    });

    // Handle coin_transaction from addPost (e.g. Prima & Dopo share)
    builder.addCase(addPost.fulfilled, (state, action) => {
      if (action.payload?.coin_transaction) {
        const { id, balance_after, points } = action.payload.coin_transaction;
        const request = action.meta?.arg || {};
        const hasVideo =
          Array.isArray(request.videos) && request.videos.some(Boolean);
        const isVideoProcessing =
          hasVideo ||
          Number(request.status_id) === POST_STATUS_ID.PROCESSING;

        state.coins.balance = balance_after;
        state.coins.lastTransaction = {
          id,
          points,
          balance_after,
          action: action.payload.coin_transaction.action,
          source: "addPost",
          isVideoProcessing,
          // Navigation target so the coin popup's "Continua" can take the user
          // straight to the post they just published in the community feed.
          postGroupId: action.payload.post_group_id ?? null,
          groupId: action.payload.group_id ?? null,
        };
      }
    });

    // Handle coin_transaction from challenge activity confirmation
    builder.addCase(recordChallengeActivity.fulfilled, (state, action) => {
      const coinTx = action.payload?.coin_transaction;
      // The final bonus is credited after the day's coins (since 30/10/2026 it
      // can come on any day, not only the last): the header shows the balance
      // after both.
      const bonusTx = action.payload?.bonus_coin_transaction;

      if (coinTx?.balance_after !== undefined) {
        state.coins.balance = bonusTx?.balance_after ?? coinTx.balance_after;
        state.coins.lastTransaction = {
          points: coinTx.points,
          balance_after: coinTx.balance_after,
          action: coinTx.action,
        };
        return;
      }

      if (action.payload?.coin_balance !== undefined) {
        state.coins.balance = action.payload.coin_balance;
      }
    });
  },
});

export const {
  setAdoc_t,
  setAdoc_p,
  setAdoc_agreements,
  updateUserProperty,
  markPremiumOptimistic,
  resetPostWellnessIndexError,
  LogoutUser,
  updateUserModal,
  updateCoinBalance,
  setAppleRecoverPending,
} = userSlice.actions;
export default userSlice.reducer;
